import { useState, useEffect } from "react";
import axios from "axios";
import { Box, Card, CardContent, Button, TextField, Typography } from "@mui/material";
import { motion } from "framer-motion";

export default function RegistrationForm() {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    age: "",
    gender: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [registrationOpen, setRegistrationOpen] = useState(true);

  // Check current registrations
  useEffect(() => {
    axios
      .get("https://sport-rex.com/race/API/participants.php")
      .then((res) => {
        if (res.data.length >= 110) setRegistrationOpen(false);
      })
      .catch((err) => console.error(err));
  }, []);

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMessage("");

    try {
      const res = await axios.post(
        "https://sport-rex.com/race/API/participants.php",
        form,
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": "Bearer s9h28shd92_!RandomSecureKey_92hs7d",
          },
        }
      );

      if (res.data.success) {
        setSuccessMessage("Registration successful!");
        setTimeout(() => {
          // ✅ Redirect to success page
          window.location.href = "https://sport-rex.com/race/rex/success";
        }, 1500);

        setForm({
          first_name: "",
          last_name: "",
          email: "",
          phone: "",
          age: "",
          gender: "",
          notes: "",
        });
      } else {
        alert(res.data.message || "Failed to submit registration");
        if (res.data.message?.includes("closed")) setRegistrationOpen(false);
      }
    } catch (err) {
      console.error(err);
      alert("Error submitting registration. This may be a network/CORS issue if testing locally.");
    }

    setLoading(false);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        position: "relative",
        overflow: "hidden",
        pb: 12,
      }}
    >
      {/* Top Banner */}
      <Box
        component="img"
        src="https://sport-rex.com/race/Content/top_banner.png"
        sx={{
          width: "100%",
          height: "auto",
          maxHeight: 200,
          objectFit: "contain",
          display: "block",
        }}
      />

      {/* Race Info */}
      <Box sx={{ width: "100%", maxWidth: 800, textAlign: "center", mt: 2, px: 2 }}>
        <Card sx={{ bgcolor: "#f0f0f0", p: 3, boxShadow: 3 }}>
          <Typography variant="h5" color="primary" gutterBottom>
            REx’s National Day Race in Safwa
          </Typography>
          <Typography variant="body1" color="textSecondary" gutterBottom>
            Celebrate Saudi National Day with us! Join the fun run, enjoy the activities, and be part of the festivities.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            📅 Date: September 23, 2025 | 🕖 Time: 4:00 PM | 📍 Location: Safwa Club
          </Typography>
        </Card>
      </Box>

      {/* Registration Form */}
      <Box sx={{ width: "100%", display: "flex", justifyContent: "center", mt: 3, px: 2 }}>
        <Card sx={{ width: "100%", maxWidth: 600, bgcolor: "rgba(255,255,255,0.95)", boxShadow: 5 }}>
          <CardContent sx={{ p: 4 }}>
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              style={{ textAlign: "center", marginBottom: "24px" }}
            >
              <img
                src="https://sport-rex.com/wp-content/uploads/2024/04/websitelogo2.png"
                alt="REx Logo"
                style={{ width: "150px", marginBottom: "16px" }}
              />
              <Typography variant="h5" color="primary" gutterBottom>
                Registration Form
              </Typography>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={form.first_name}
                  onChange={(e) => handleChange("first_name", e.target.value)}
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  label="Last Name"
                  value={form.last_name}
                  onChange={(e) => handleChange("last_name", e.target.value)}
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={form.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  label="Phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  label="Age"
                  type="number"
                  value={form.age}
                  onChange={(e) => handleChange("age", e.target.value)}
                  margin="normal"
                  required
                />

                <Box mt={2}>
                  <label htmlFor="gender">Gender</label>
                  <select
                    id="gender"
                    value={form.gender}
                    onChange={(e) => handleChange("gender", e.target.value)}
                    required
                    style={{
                      width: "100%",
                      padding: "10px",
                      marginTop: "5px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                    }}
                  >
                    <option value="">Select</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </Box>

                <TextField
                  fullWidth
                  label="Notes"
                  value={form.notes}
                  onChange={(e) => handleChange("notes", e.target.value)}
                  margin="normal"
                  multiline
                  rows={3}
                />

                {successMessage && (
                  <Typography mt={2} color="green" textAlign="center">
                    {successMessage}
                  </Typography>
                )}

                <Box mt={3} textAlign="center">
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={loading || !registrationOpen}
                  >
                    {registrationOpen ? (loading ? "Registering..." : "Register") : "Registration Closed"}
                  </Button>
                </Box>
              </form>
            </motion.div>
          </CardContent>
        </Card>
      </Box>

      {/* Footer */}
      <Box
        component="img"
        src="https://sport-rex.com/race/Content/Footer.png"
        alt="Footer"
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100vw",
          height: 120,
          objectFit: "cover",
          zIndex: -1,
        }}
      />
    </Box>
  );
}
